import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'components/Shared/responsive';
import { Section } from 'components/Section';
import { Addons } from 'views/ProductPage/addons';
import { Media } from './media';
import { Header } from './header';
import { AddToCart } from './addToCart';
import { VariantSelector } from 'views/ProductPage/themes/BGH/sections/variantSelector';
import { ProductShortDescription } from '../BGH/sections/productShortDescription';
import { ProductDescription } from '../BGH/sections/productDescription';
import { ProductAccordion } from '../BGH/productAccordion';
import { StandaloneProductAttributes } from '../BGH/sections/productAttributes';
import { ProductSection } from '../BGH/sections/productSection';
import { Accessories } from 'views/ProductPage/accessories';
import { OthersAlsoBoughtProducts } from 'views/ProductPage/othersAlsoBoughtProducts';
import { Recommended } from 'views/ProductPage/recommended';
import ProductsInSeries from '../BGH/sections/productsInSeries';
import { useLoadAdditionalData } from 'views/ProductPage/useLoadAdditionalData';
import { DesktopPackageProduct } from '../BGH/sections/packageProduct';
import { ProductPageStructuredData } from 'views/ProductPage/productPageStructuredData';
import { Reviews } from './reviews';
import { FAQ } from './FAQ';
import { rm, withCurrentPage } from 'utils';
import styles from './styles.scss';
import { getProductId, topContentAreaSelector } from 'views/ProductPage/selector';
import { ContentArea } from 'components/Shared/Markdown';
import { ProductPageModel } from 'models/ProductPageModel';
import { AdminProductAttributes } from '../BGH/sections/adminProductAttributes';
import { AddonServices } from 'views/ProductPage/addonServices';
import { productPageLoaded } from 'views/ProductPage/actions';
import { useEffect } from 'react';

type ProductPageProps = Pick<ProductPageModel, 'topContentArea'> & {
  productId: string;
};

const ProductPage = () => {
  const isPhone = useSelector(isMobile);
  const productId = useSelector(withCurrentPage(getProductId));
  const topContentArea = useSelector(topContentAreaSelector);
  const reduxDispatch = useDispatch();

  useLoadAdditionalData();

  useEffect(() => {
    // GTM event for every new product (variants handled separately)
    reduxDispatch(productPageLoaded());
  }, [reduxDispatch, productId]);

  return (
    <>
      {isPhone ? (
        <ProductPageMobile productId={productId} topContentArea={topContentArea} />
      ) : (
        <ProductPageDesktop productId={productId} topContentArea={topContentArea} />
      )}
      <ProductPageStructuredData />
    </>
  );
};

const ProductPageMobile = ({ productId, topContentArea }: ProductPageProps) => (
  <div>
    <div className={styles.whiteBackground}>
      {topContentArea && <ContentArea wrappingComponent={<Section />} {...topContentArea} />}
      <Media />
      <Section>
        <Header />
      </Section>
      <VariantSelector key={productId} />
      <AddonServices />
      <Addons className={styles.addons} />
      <Section>
        <AddToCart />
      </Section>
      <ProductAccordion />
    </div>
    <Section full>
      <ProductsInSeries />
      <Accessories />
      <OthersAlsoBoughtProducts />
      <Recommended />
    </Section>
  </div>
);

const ProductPageDesktop = ({ productId, topContentArea }: ProductPageProps) => (
  <div className={styles.desktop}>
    {topContentArea && <ContentArea wrappingComponent={<Section />} {...topContentArea} />}
    <Section className={styles.section} contentClassName={cn(styles.sectionContent, styles.top)}>
      <div className={styles.left}>
        <Media />
      </div>
      <div className={styles.right}>
        <Header />
        <VariantSelector key={productId} />
        <AddonServices />
        <Addons className={styles.addons} />
        <AddToCart />
        <DesktopPackageProduct />
      </div>
      <AdminProductAttributes />
    </Section>
    <Section className={styles.section} contentClassName={styles.descriptionAndAttributes}>
      <div className={styles.description}>
        <ProductSection title={rm('product_description_header')}>
          <ProductShortDescription hideAttributes hideBrand />
          <ProductDescription />
        </ProductSection>
        <FAQ />
        <Reviews />
      </div>
      <div className={styles.attributes}>
        <StandaloneProductAttributes />
      </div>
    </Section>

    <Section>
      <ProductsInSeries />
      <Accessories />
      <OthersAlsoBoughtProducts />
      <Recommended />
    </Section>
  </div>
);

export default ProductPage;
